import { isArray, isString } from '@mbrtargeting/metatag-utils';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { onPhase, Phase } from '../../../loader/decorators/on-phase.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { selectModuleSettings } from '../../../loader/essentials/config-selector.js';
import { logger } from '../../../loader/essentials/logger.js';
import { ConsentCache } from '../../../loader/modules/consent-cache.js';
import { CONFIG_RESOLVER, CONSENT_CACHE } from '../../../loader/token.js';
import { sendCorsRequestPromise } from '../../../utils/send-cors-request.js';
import { GLOBAL_TARGETING, ID_STORE } from '../../token.js';
import { GlobalTargeting } from '../global-targeting/global-targeting.js';
import { IdStore } from '../idstore/idstore.js';

const log = logger.logGroup({ prefix: 'MbrId' });

interface MbrIdResponse {
    mbrUserId?: string,
    mbrSegments?: string[],
    mbrCategories?: string[],
    mbrSegmentsTest?: string[],
}

@injectionTarget()
export class MbrId {

    @inject(CONFIG_RESOLVER) private configResolver!: ConfigResolver;
    @inject(CONSENT_CACHE) private consentCache!: ConsentCache;
    @inject(ID_STORE) private idStore!: IdStore;
    @inject(GLOBAL_TARGETING) private globalTargeting!: GlobalTargeting;

    /**
     * requests the mbrId and writes it to storage. If the request fails it writes the id as "failed" and sends it anyway
     *
     * @example response
     * {
     *     "mbrUserId": "ff0da6f1912c385d8f1d82e9bc9c2114",
     *     "mbrSegments": ["855125d3", "b3838f1f", "7a97658b", "8a1a9d78", "980f3875", "825a837e"],
     *     "mbrCategories": ["c1347","7afb3","29e18","88f1a","2500f"]
     * }
     */
    @onPhase({ phase: Phase.TARGETING })
    async requestUserId() {
        const tcData = await this.consentCache.getTcDataPromise();
        const gdprApplies: boolean = tcData?.gdprApplies || false;
        const consentString = tcData?.tcString || '';
        const { iabVendorId = 0, active = false, url = '' } = this.configResolver.get(selectModuleSettings('MBR_USER_ID')) || {};
        const isAllowed = this.consentCache.ifConsentConditions([
            { consentedVendors: [iabVendorId] },
            { legitimateInterestVendors: [iabVendorId] },
            { gdprApplies: false },
        ]);

        if (!active || url.length === 0 || !isAllowed) return;

        const referrer = location.protocol + '//' + location.hostname + location.pathname; //without queries
        const src = `${url}&gdpr=${gdprApplies.toString()}&gdpr_consent=${consentString}&ref=${encodeURI(referrer)}`;

        const safeValues = (x: unknown) => isArray(x) && x.every(isString) ? x : [];
        await sendCorsRequestPromise<MbrIdResponse>({
            src,
            withCredentials: true,
            // onsuccsess
        }).then(({ mbrUserId, mbrSegments, mbrCategories, mbrSegmentsTest }) => {
            const id: string = (isString(mbrUserId) && mbrUserId.length > 0) ? mbrUserId : 'default';
            this.idStore.setIdPromise('mbrid', id).then(() => {
                log.debug('saved id "%s"', [id]);
            });
            this.globalTargeting.setKeyValues({
                mbrid: [id],
                mbrseg: safeValues(mbrSegments),
                mbrct: safeValues(mbrCategories),
                mbrsegtest: safeValues(mbrSegmentsTest),
            });
        }).catch((error) => {
            log.debug('CORS request for mbrUserId failed, error:', [error]);
            const id = 'failed';
            this.idStore.setIdPromise('mbrid', id).then(() => {
                log.debug('saved id "%s"', [id]);
            });
            this.globalTargeting.setValues('mbrid', [id]);
        });
    }
}
